import React, { createContext, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { registerTranslationForDebug } from './translationDebugUtils';
import type { TranslationProviderProps, TranslationFunction } from './types';
import type {
    KeyPrefix,
    PrefixedTranslationKey,
    TranslationGroup,
    TranslationKey,
} from '../../../common/locales/types/translationTypes';

type GroupTranslationContextType = {
    group: TranslationGroup;
    keyPrefix?: string;
    t: (key: string, options?: Record<string, unknown>) => string;
};

const GroupTranslationContext = createContext<GroupTranslationContextType | null>(null);

/**
 * Provider that sets up translation context for a file/component tree.
 * Child components can access the translation function via the useGroupTranslator() hook.
 *
 * The translation function is properly typed to only accept valid keys for the given group/prefix.
 *
 * Usage:
 *   <GroupTranslatorProvider group="elements" keyPrefix="elementTypes.document">
 *     <MyComponent />
 *   </GroupTranslatorProvider>
 *
 * In MyComponent:
 *   const translate = useGroupTranslator();
 *   const text = translate('key', 'default');
 */
export function GroupTranslatorProvider<
    Group extends TranslationGroup,
    Prefix extends KeyPrefix<Group> | undefined = undefined,
>({ group, keyPrefix, children }: TranslationProviderProps<Group> & { keyPrefix?: Prefix }): React.ReactElement {
    const { t: i18nT } = useTranslation(
        group as never,
        (keyPrefix ? { keyPrefix: keyPrefix as string } : {}) as never,
    ) as {
        t: (key: string, options?: Record<string, unknown>) => string;
    };

    // Wrap the translation function to add debug registration
    const t = useCallback(
        (key: string, options?: Record<string, unknown>) => {
            const result = i18nT(key, options);
            const translatedText = typeof result === 'string' ? result : String(result);
            const fullTranslationKey = keyPrefix ? `${keyPrefix}.${key}` : key;
            registerTranslationForDebug(translatedText, group, fullTranslationKey);
            return translatedText;
        },
        [i18nT, group, keyPrefix],
    );

    const contextValue = React.useMemo(
        () => ({
            group,
            keyPrefix,
            t,
        }),
        [group, keyPrefix, t],
    );

    return <GroupTranslationContext.Provider value={contextValue}>{children}</GroupTranslationContext.Provider>;
}

/**
 * Type-safe translation function for context-based translator (no options parameter)
 */
type GroupTranslationFunction<G extends TranslationGroup, P extends KeyPrefix<G> | undefined = undefined> =
    P extends KeyPrefix<G>
        ? <K extends PrefixedTranslationKey<G, P>>(
              key: K,
              defaultValue: string,
              context?: Record<string, unknown>,
          ) => string
        : <K extends TranslationKey<G>>(key: K, defaultValue: string, context?: Record<string, unknown>) => string;

/**
 * Hook to get translation function from GroupTranslatorProvider context
 *
 * Usage:
 *   const translate = useGroupTranslator();                                      // No type checking
 *   const translate = useGroupTranslator<'elements'>();                          // Type-checked for group
 *   const translate = useGroupTranslator<'elements', 'elementTypes.document'>(); // Type-checked for prefix
 */
export function useGroupTranslator(): TranslationFunction;
export function useGroupTranslator<
    G extends TranslationGroup,
    P extends KeyPrefix<G> | undefined = undefined,
>(): GroupTranslationFunction<G, P>;
export function useGroupTranslator(): TranslationFunction {
    const translationContext = useContext(GroupTranslationContext);

    const group = translationContext?.group;
    const keyPrefix = translationContext?.keyPrefix;
    const t = translationContext?.t;

    // Always call useCallback - don't violate Rules of Hooks
    return useCallback(
        (translationKey: string, defaultValue: string, context?: Record<string, unknown>): string => {
            // FIXME-TRANSLATIONS - Implement proper error handling at the end of the migration
            if (!translationContext || !t || !group) {
                return defaultValue;
            }

            try {
                const result = t(translationKey, { ...context, defaultValue });
                const translatedText = typeof result === 'string' ? result : String(result);

                const fullTranslationKey = keyPrefix ? `${keyPrefix}.${translationKey}` : translationKey;
                registerTranslationForDebug(translatedText, group, fullTranslationKey);

                return translatedText;
            } catch (error) {
                // FIXME-TRANSLATIONS - Implement proper error handling at the end of the migration
                return defaultValue;
            }
        },
        [t, group, keyPrefix],
    ) as TranslationFunction;
}
